// plugins/02.cookieyesConsent.client.ts
import type { DataLayerObject } from '@gtm-support/core'

declare global {
	interface Window {
		cookieyes?: { _ckyConsentStore?: Map<string, string> }
		dataLayer?: DataLayerObject[]
		gtm_enable_debug?: boolean
		requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number
	}
}

let lastConsentSnapshot = ''
let lastAnalyticsConsent: 'granted' | 'denied' | null = null
let sentFirstPageView = false
let spaHooked = false
let consentInitialized = false
let adsConsentGrantedPushed = false

const gtag = (...args: any[]) => {
	window.dataLayer = window.dataLayer || []
	window.dataLayer.push(args)
}

const isProdHost = () => {
	const host = window.location.hostname
	return host === 'luckywise360.com' || host === 'www.luckywise360.com'
}

const isPreviewMode = () => {
	const qs = new URLSearchParams(window.location.search)
	return qs.has('gtm_preview') || qs.has('gtm_debug') || qs.has('gtm_auth') || window.gtm_enable_debug === true
}

const pushPageView = (path?: string) => {
	window.dataLayer = window.dataLayer || []
	window.dataLayer.push({
		event: 'page_view',
		page_location: window.location.href,
		page_title: document.title,
		page_path: path ?? window.location.pathname + window.location.search + window.location.hash,
	})
}

const setDefaultConsentOnce = () => {
	const alreadyDefault = window.dataLayer?.some(
		(e: any) => Array.isArray(e) && e[0] === 'consent' && e[1] === 'default',
	)
	if (alreadyDefault) return

	gtag('consent', 'default', {
		ad_storage: 'denied',
		analytics_storage: 'denied',
		functionality_storage: 'granted',
		personalization_storage: 'denied',
		security_storage: 'granted',
		ad_user_data: 'denied',
		ad_personalization: 'denied',
	})
}

const getCookieYesConsent = async () => {
	return await new Promise<Map<string, string> | null>((resolve) => {
		let attempts = 0
		const tryGet = () => {
			const store = window.cookieyes?._ckyConsentStore
			if (store instanceof Map) {
				resolve(store)
				return
			}
			if (attempts++ >= 20) {
				resolve(null)
				return
			}
			setTimeout(tryGet, 250)
		}
		tryGet()
	})
}

const loadGtmScriptOnce = (gtmId: string) => {
	if (!gtmId) return
	if (document.querySelector('script[data-gtm="true"]')) return

	const script = document.createElement('script')
	script.async = true
	script.setAttribute('data-gtm', 'true')
	script.dataset.gtm = 'true'
	script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`
	document.head.appendChild(script)
}

// ─── NUEVO: emite ads_consent_granted de forma desacoplada ───────────────────
const pushAdsConsentGranted = () => {
	window.dataLayer = window.dataLayer || []
	window.dataLayer.push({
		event: 'ads_consent_granted',
		page_location: window.location.href,
		page_title: document.title,
		page_path: window.location.pathname + window.location.search + window.location.hash,
		ads_consent: 'granted',
	})
}

export default defineNuxtPlugin((nuxtApp) => {
	if (!import.meta.client) return

	const router = useRouter()

	const hookSpaPageViewsOnce = (canTrackAnalytics: () => boolean) => {
		if (spaHooked) return
		spaHooked = true
		router.afterEach((to) => {
			if (canTrackAnalytics()) pushPageView(to.fullPath)
		})
	}

	const initializeConsent = async () => {
		if (consentInitialized) return
		consentInitialized = true

		const { public: pub } = useRuntimeConfig()
		const envName = String(pub.envName || '')
		const isProduction = envName === 'production'
		const gtmEnabled = String(pub.gtm?.enabled) === 'true'
		const gtmId = String(pub.gtm?.id || '')
		const debugPreview = String(pub.gtm?.debug || 'false') === 'true'
		const prodHost = isProdHost()
		const previewMode = isPreviewMode()

		const allowPlugin = (isProduction && prodHost && gtmEnabled) || (!prodHost && debugPreview && previewMode)
		if (!allowPlugin || !gtmId) return

		window.dataLayer = window.dataLayer || []
		setDefaultConsentOnce()
		loadGtmScriptOnce(gtmId)

		let currentConsent: Map<string, string> | null = null

		const canTrackAnalytics = () => {
			const consentGranted = currentConsent?.get('analytics') === 'yes'
			const qaOverride = !prodHost && debugPreview && previewMode
			return consentGranted || qaOverride
		}

		const canTrackAds = () => currentConsent?.get('advertisement') === 'yes'

		const applyConsent = () => {
			const adConsent: 'granted' | 'denied' = canTrackAds() ? 'granted' : 'denied'
			const analyticsConsent: 'granted' | 'denied' = canTrackAnalytics() ? 'granted' : 'denied'

			const snap = JSON.stringify({ adConsent, analyticsConsent })
			if (snap === lastConsentSnapshot) return

			const analyticsBecameGranted = lastAnalyticsConsent !== 'granted' && analyticsConsent === 'granted'

			lastConsentSnapshot = snap
			lastAnalyticsConsent = analyticsConsent

			if (adConsent === 'denied') adsConsentGrantedPushed = false

			gtag('consent', 'update', {
				ad_storage: adConsent,
				analytics_storage: analyticsConsent,
				functionality_storage: 'granted',
				personalization_storage: 'denied',
				security_storage: 'granted',
				ad_user_data: adConsent,
				ad_personalization: adConsent,
			})

			if (analyticsConsent === 'granted') {
				if (!sentFirstPageView) {
					sentFirstPageView = true
					pushPageView()
				}
				hookSpaPageViewsOnce(canTrackAnalytics)
			}

			if (analyticsBecameGranted) {
				window.dataLayer?.push({ event: 'gtm.consentGranted' })
			}
		}

		// ─── NUEVO: controla ads_consent_granted respetando el modal de edad ────
		// Se llama cada vez que hay anuncio granted. Si el modal de edad está
		// visible, espera a que desaparezca antes de emitir el evento.
		const maybeEmitAdsConsent = () => {
			if (adsConsentGrantedPushed) return
			adsConsentGrantedPushed = true

			const ageModalSelector = '#ageVerificationModal, [data-age-modal], .age-modal'
			const ageModal = document.querySelector(ageModalSelector) as HTMLElement | null

			const isModalVisible = (el: HTMLElement | null) => el !== null && el.offsetParent !== null && !el.hidden

			if (!isModalVisible(ageModal)) {
				// Modal no existe o ya está cerrado → emitir inmediatamente
				setTimeout(pushAdsConsentGranted, 500)
				return
			}

			// Modal visible → observar hasta que desaparezca
			const modalObserver = new MutationObserver(() => {
				if (!isModalVisible(ageModal)) {
					modalObserver.disconnect()
					setTimeout(pushAdsConsentGranted, 300)
				}
			})
			modalObserver.observe(ageModal!, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] })

			// También observar si el modal se elimina del DOM
			const parentObserver = new MutationObserver(() => {
				if (!document.contains(ageModal) || !isModalVisible(ageModal)) {
					parentObserver.disconnect()
					modalObserver.disconnect()
					setTimeout(pushAdsConsentGranted, 300)
				}
			})
			parentObserver.observe(document.body, { childList: true, subtree: true })
		}

		const refreshConsent = () => {
			const store = window.cookieyes?._ckyConsentStore
			if (!(store instanceof Map)) return

			currentConsent = store

			if (store.get('advertisement') === 'yes') {
				maybeEmitAdsConsent()
			}

			applyConsent()
		}

		// ─── Listeners de eventos (lo antes posible) ─────────────────────────────
		window.addEventListener('cookieyes_consent_update', refreshConsent)
		window.addEventListener('CookieYesConsentUpdate', refreshConsent)

		// ─── MutationObserver (mantener como está, funciona en desktop/Android) ──
		const observer = new MutationObserver(refreshConsent)
		observer.observe(document.body, { childList: true, subtree: true })

		// ─── NUEVO: Polling como fallback para iOS/Safari ─────────────────────────
		// Safari a veces no dispara los eventos y el MutationObserver no detecta
		// cambios en el Shadow DOM de CookieYes. El polling cubre ambos casos.
		let lastPollSnapshot = ''
		const pollInterval = setInterval(() => {
			const store = window.cookieyes?._ckyConsentStore
			if (!(store instanceof Map)) return

			// Comparar snapshot para no ejecutar refreshConsent innecesariamente
			const snap = JSON.stringify([store.get('analytics'), store.get('advertisement'), store.get('functional')])
			if (snap === lastPollSnapshot) return
			lastPollSnapshot = snap

			refreshConsent()
		}, 300)

		// Parar el polling tras 30s (CookieYes siempre habrá cargado para entonces)
		setTimeout(() => clearInterval(pollInterval), 30_000)

		// ─── Leer estado inicial ──────────────────────────────────────────────────
		currentConsent = await getCookieYesConsent()
		applyConsent()
	}

	void initializeConsent()
	nuxtApp.hook('app:mounted', () => {
		void initializeConsent()
	})
})
